// React and MobX
import React, { useState } from "react";
import { useObserver } from "mobx-react-lite";

// Material-UI Core and Styles
import { Box, FormControlLabel, MenuItem, Radio, Checkbox, Select, Tooltip, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

// Core
import { KeyValuePair } from "Core/Models/KeyValuePair";

// Custom Components
import Lightbox from "Custom/Components/Popups/Lightbox";
import { EditableInput } from "Custom/Components/Edits/EditableInput";
import { PrivateBlob } from "Custom/Components/Image/PrivateImage";

// Custom Styles
import { ChoicesProductGroupStyles, GridContainerCounter } from "Custom/StylesAppSpecific/DashboardSubViews/ChoicesProductGroupStyling";

// Custom ViewModels
import { ProductViewModel } from "Custom/ViewModels/DashboardSubViewModels/ProductViewModel";
import { ProductGroupViewModel } from "Custom/ViewModels/DashboardSubViewModels/ProductGroupViewModel";
import { Format } from "Custom/Utils";
import { multiply } from "lodash-es";

interface IProps {
    viewModel: ProductViewModel;
    productGroupViewModel: ProductGroupViewModel;
    index: number;
    isExtra: boolean;
    isDisabled: boolean;
}

export const ChoicesProduct: React.FC<IProps> = props => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;
    const [productImage, setProductImage] = useState("");
    const [isProductImgOpen, setIsProductImageOpen] = useState(false);
    const [productVariantErrorMessage, setProductVariantErrorMessage] = useState("");
    const choicesProductGroupStyles = ChoicesProductGroupStyles();

    const [isNegativePrice, setIsNegativePrice] = useState(false);

    const inputCSSClassNames = (defaultCSS: string) => {
        let CSSRetVal = defaultCSS;

        if (props.isDisabled) {
            CSSRetVal += " choices-product__input--disabled";
        }

        return CSSRetVal;
    };

    const handleProductIsSelectOption = () => {
        setProductVariantErrorMessage("");
        if (!props.isDisabled) {
            if (props.viewModel.hasVariants) {
                setProductVariantErrorMessage("Please select a variant");
            }
            props.productGroupViewModel.setProductIsSelected(props.viewModel);
        }
    };

    const handleProductIsSelectExtra = () => {
        setProductVariantErrorMessage("");
        if (!props.isDisabled) {
            if (props.viewModel.hasVariants && !props.viewModel.model.isSelected) {
                setProductVariantErrorMessage("Please select a variant");
            }
            props.productGroupViewModel.setProductExtraIsSelected(props.viewModel, !props.viewModel.model.isSelected);
        }
    };

    const handleSelectProductVariant = (val: string) => {
        setProductVariantErrorMessage("");
        if (!props.isDisabled) {
            props.productGroupViewModel.selectProductVariant(val, props.viewModel);
        }
    };

    const getExtraTotalPrice = () => {
        const extraPrice = props.viewModel.model.extraPrice;
        if (!extraPrice || extraPrice === "" || extraPrice.toUpperCase() === "£POA" || extraPrice.toUpperCase() === "POA") {
            return extraPrice || "";
        }

        const quantity = props.viewModel.model.orderQuantity || 1;
        if (quantity <= 1) {
            return extraPrice;
        }

        // Parse the price string (remove £ and commas)
        const numericPrice = parseFloat(Format.currencyFormatForUpsert(extraPrice));
        if (isNaN(numericPrice)) {
            return extraPrice;
        }

        // Calculate total
        const totalPrice = multiply(numericPrice, quantity);
        return `£${Format.currencyFormat(totalPrice.toString(), true)}`;
    };

    const renderPriceLabel = (perProductPrice: string | undefined, totalPrice: string | null, showBothPrices: boolean) => {
        const tooltipText =
            "The price for this product is a negative value because the price set against the 'included' product is greater than the price of the 'upgrade' product for this specification.";

        const getPriceWithTooltip = (price: string | undefined) => {
            if (!price) return "";

            // reset flag
            let isNegative = false;

            const numeric = parseInt(price.replace("£", "").replace(/,/g, ""));
            if (!isNaN(numeric) && Math.sign(numeric) === -1) {
                isNegative = true;
            }

            return isNegative ? (
                <Box className={choicesProductGroupStyles.priceWrapper}>
                    <Tooltip
                        title={tooltipText}
                        arrow
                        placement="top-start"
                        classes={{
                            tooltip: choicesProductGroupStyles.negativePriceTooltip,
                            arrow: choicesProductGroupStyles.negativePriceTooltipArrow,
                        }}
                    >
                        <span className={choicesProductGroupStyles.tooltipTrigger}>
                            <span className={choicesProductGroupStyles.priceTip}>!</span>
                        </span>
                    </Tooltip>
                    {price}
                </Box>
            ) : (
                price
            );
        };

        //Case: show both prices (per unit + total)
        if (showBothPrices && totalPrice && totalPrice !== perProductPrice) {
            return (
                <Box
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        padding: "2px",
                        marginRight: "12px",
                        whiteSpace: "nowrap",
                        alignItems: "flex-end",
                    }}
                >
                    <Typography variant="body2" component="span" style={{ lineHeight: "1.5" }}>
                        {getPriceWithTooltip(perProductPrice)}
                    </Typography>

                    <Typography variant="body2" component="span" style={{ fontWeight: "bold", lineHeight: "1.5" }}>
                        Total: {getPriceWithTooltip(totalPrice)}
                    </Typography>
                </Box>
            );
        }

        //Default case
        const displayPrice = totalPrice ? totalPrice : perProductPrice || "";

        return (
            <Typography className={choicesProductGroupStyles.productLabel} variant="body2" style={{ display: "flex", marginTop: "4px" }}>
                {getPriceWithTooltip(displayPrice)}
            </Typography>
        );
    };

    return useObserver(() => (
        <Box className={choicesProductGroupStyles.productContainer}>
            <Box className={choicesProductGroupStyles.productContentRow}>
                <Box className={choicesProductGroupStyles.productImageContainer}>
                    <PrivateBlob
                        alt={props.viewModel.model.productName}
                        src={props.viewModel.productImage}
                        onClick={() => {
                            if (props.viewModel.productOriginalImage) {
                                setProductImage(props.viewModel.productOriginalImage);
                                setIsProductImageOpen(true);
                            }
                        }}
                    />
                </Box>
                <Box className={choicesProductGroupStyles.productContentContainer}>
                    <Box>
                        <Typography className={choicesProductGroupStyles.productTitle} variant="body2">
                            {props.viewModel.productTitle}
                        </Typography>
                        <Typography className={choicesProductGroupStyles.productDescription} variant="body2">
                            {props.viewModel.model.productDescription}
                        </Typography>
                    </Box>
                    <Box>
                        {props.viewModel.model.isQuantityAllowed && (
                            <GridContainerCounter style={{ marginRight: "10px", flexDirection: "column" }}>
                                <label className={choicesProductGroupStyles.counterLabel}>Quantity:</label>
                                <EditableInput
                                    className={choicesProductGroupStyles.counterInput}
                                    type="number"
                                    fieldName="orderQuantity"
                                    viewModel={props.viewModel}
                                    onChange={props.viewModel.model.onQuantityChange}
                                    inputProps={{
                                        id: "orderQuantity-input",
                                    }}
                                />
                            </GridContainerCounter>
                        )}
                        {props.viewModel.hasVariants && (
                            <Box className={choicesProductGroupStyles.productVariantsContainer} key={`product-variants-list ${props.index}`}>
                                <Select
                                    MenuProps={{
                                        disableScrollLock: true,
                                    }}
                                    variant="outlined"
                                    onChange={e => handleSelectProductVariant(e.target.value as string)}
                                    placeholder={!props.isDisabled ? "Please select variant..." : ""}
                                    className={choicesProductGroupStyles.productVariantSelect}
                                    value={props.viewModel.getSelectedVariant}
                                >
                                    {props.viewModel.productVariantsOptions!.map((item: KeyValuePair, index: number) => {
                                        return (
                                            <MenuItem disabled={item.value === ""} key={item.value} value={item.value}>
                                                {item.key}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                                {productVariantErrorMessage !== "" && (
                                    <Typography variant="caption" style={{ color: "red" }}>
                                        {productVariantErrorMessage}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box>
                {props.viewModel.isOption && (
                    // <FormControlLabel
                    //     control={<Radio className={choicesProductGroupStyles.radioButton} />}
                    //     checked={props.viewModel.model.isSelected}
                    //     // label={
                    //     //     <Typography variant="body2">
                    //     //         {props.viewModel.model.isQuantityAllowed && props.viewModel.model.getPriceOnAddQuantity()
                    //     //             ? props.viewModel.model.getPriceOnAddQuantity()
                    //     //             : props.viewModel.model.price}
                    //     //     </Typography>
                    //     // }
                    //     label={(() => {
                    //         const model = props.viewModel.model;

                    //         const perProductPrice = model.price || "";

                    //         const calculatedTotal = model.getPriceOnAddQuantity();

                    //         const totalPrice = model.isQuantityAllowed && calculatedTotal ? calculatedTotal : null;

                    //         const showBothPrices = model.isQuantityAllowed && model.orderQuantity > 1;

                    //         return renderPriceLabel(perProductPrice, totalPrice, showBothPrices);
                    //     })()}
                    //     labelPlacement={isDesktop ? "start" : "end"}
                    //     name={`${props.productGroupViewModel.model.productGroupName + props.index}`}
                    //     value={props.viewModel.model.productId}
                    //     onChange={() => handleProductIsSelectOption()}
                    //     className={inputCSSClassNames(props.viewModel.model.isSelected ? choicesProductGroupStyles.productRadioChecked : choicesProductGroupStyles.productRadio)}
                    // />

                    <Box
                        style={{ display: "flex", alignItems: "flex-start" }}
                        className={inputCSSClassNames(props.viewModel.model.isSelected ? choicesProductGroupStyles.productRadioChecked : choicesProductGroupStyles.productRadio)}
                    >
                        {(() => {
                            const model = props.viewModel.model;

                            const perProductPrice = model.price || "";

                            const calculatedTotal = model.getPriceOnAddQuantity();

                            const totalPrice = model.isQuantityAllowed && calculatedTotal ? calculatedTotal : null;

                            const showBothPrices = model.isQuantityAllowed && model.orderQuantity > 1;

                            return renderPriceLabel(perProductPrice, totalPrice, showBothPrices);
                        })()}
                        <Radio
                            className={choicesProductGroupStyles.radioButton}
                            checked={props.viewModel.model.isSelected}
                            name={`${props.productGroupViewModel.model.productGroupName + props.index}`}
                            value={props.viewModel.model.productId}
                            onChange={() => handleProductIsSelectOption()}
                        />
                    </Box>
                )}

                {props.viewModel.isExtra && (
                    <Box
                        style={{ display: "flex", alignItems: "flex-start" }}
                        className={inputCSSClassNames(
                            props.viewModel.model.isSelected ? choicesProductGroupStyles.productCheckboxChecked : choicesProductGroupStyles.productCheckbox,
                        )}
                    >
                        {renderPriceLabel(
                            props.viewModel.model.extraPrice || "",
                            props.viewModel.model.isQuantityAllowed && getExtraTotalPrice() ? getExtraTotalPrice() : null,
                            props.viewModel.model.isQuantityAllowed && props.viewModel.model.orderQuantity > 1,
                        )}
                        <Checkbox
                            className={choicesProductGroupStyles.checkboxButton}
                            checked={props.viewModel.model.isSelected}
                            name={`${props.productGroupViewModel.model.productGroupName + props.index}-extra`}
                            value={props.viewModel.model.productId}
                            onChange={e => handleProductIsSelectExtra()}
                        />
                    </Box>
                    // <FormControlLabel
                    //     control={<Checkbox className={choicesProductGroupStyles.checkboxButton} />}
                    //     checked={props.viewModel.model.isSelected}
                    //     // label={<Typography variant="body2">{props.viewModel.model.extraPrice}</Typography>}
                    //     label={renderPriceLabel(
                    //         props.viewModel.model.extraPrice || "",
                    //         props.viewModel.model.isQuantityAllowed && getExtraTotalPrice() ? getExtraTotalPrice() : null,
                    //         props.viewModel.model.isQuantityAllowed && props.viewModel.model.orderQuantity > 1,
                    //     )}
                    //     labelPlacement={isDesktop ? "start" : "end"}
                    //     name={`${props.productGroupViewModel.model.productGroupName + props.index}-extra`}
                    //     value={props.viewModel.model.productId}
                    //     onChange={e => handleProductIsSelectExtra()}
                    //     className={inputCSSClassNames(
                    //         props.viewModel.model.isSelected ? choicesProductGroupStyles.productCheckboxChecked : choicesProductGroupStyles.productCheckbox,
                    //     )}
                    // />
                )}
            </Box>
            <Lightbox isOpen={isProductImgOpen} imageUrl={productImage} onClose={setIsProductImageOpen} />
        </Box>
    ));
};

export default ChoicesProduct;
